import React, { useEffect, useMemo } from "react";
import _ from 'lodash';
import cx from "classnames";

import Slider from "src/components/slider";
import { Collection, CollectionCollaborate } from "src/types/collections";

import styles from './collaborate.module.scss';
import { Checkbox, SearchBar } from "src/components/input";
import { useSelector } from "react-redux";
import Button from "src/components/button";
import { useDispatch } from "react-redux";
import { fetchCollaboratorsForCollection, fetchUserAccessForCollection, invalidateCollectionCollaborators, updateCollaboratorsForCollection } from "src/redux/actions/collections";
import InfiniteScroll from "src/components/infiniteScroll";
import { FormCheck } from "react-bootstrap";
import EmptyMessage from "src/components/emptyMessage";
import { DEAL_ROOM_EXPERIENCE } from "src/constants/collections";
import Truncate from "src/components/truncate";
import LinesEllipsis from "react-lines-ellipsis";
import Tooltip from "src/components/tooltip";
import { useCollectionsAdoption } from "src/redux/actions/adoption/collection";
import Text from "src/components/text";

interface UpdateState {
  [key: string]: {
    read: boolean;
    write: boolean;
  };
}

interface CollaborateDialogProps {
  show: boolean;
  collection: Collection;
  onClose: () => void;
}

export const CollaborateDialog: React.FC<CollaborateDialogProps> = ({ show, collection, onClose }) => {
  const dispatch = useDispatch();

  const { trackCollaboratorsUpdated, trackCollaborateScreenView } = useCollectionsAdoption();

  const [query, setQuery] = React.useState<string | null>('');
  const [readAll, setReadAll] = React.useState<boolean>(collection?.readAll || false);
  const [writeAll, setWriteAll] = React.useState<boolean>(collection?.writeAll || false);
  const [showCollaborators, setShowCollaborators] = React.useState<boolean>(false);
  const [changes, setChanges] = React.useState<UpdateState>({});

  const { updating, updated } = useSelector((state: any) => {
    const collaboratorState = _.get(state, `collections.collection['${collection.id}'].collaborators`);
    const updating = _.get(collaboratorState, 'updating');
    const updated = _.get(collaboratorState, 'updated');

    return {
      updating,
      updated
    }
  }, _.isEqual);

  useEffect(() => {
    setQuery('');
    setReadAll(collection?.readAll || false);
    setWriteAll(collection?.writeAll || false);
    setShowCollaborators(false);
    setChanges({});

    if(show) {
      trackCollaborateScreenView();
    }
    
  }, [show]);

  useEffect(() => {
    if (updated) {
      trackCollaboratorsUpdated(collection);
      handleClose();
    }
  }, [updated]);

  const handleQueryChange = (value) => {
    setQuery(value);
  }

  const handleFinish = () => {
    const result = {
      readAll: readAll,
      writeAll: writeAll,
      collaborators: _.map(changes, (value, key) => {
        const [entityType, id] = key.split('-');
        return {
          type: entityType,
          id: id,
          read: value.read,
          write: value.write
        }
      })
    } as any;

    dispatch(updateCollaboratorsForCollection({
      collection: collection.id!,
      request: result
    }));
  }

  const handleClose = () => {
    dispatch(invalidateCollectionCollaborators(collection.id!));
    onClose();
  }

  const handleReadAllChange = (value) => {
    setReadAll(value);
  }

  const handleWriteAllChange = (value) => {
    setWriteAll(value);
    if (value) {
      setReadAll(true);
    }
  }

  const handleChange = (collaborator: CollectionCollaborate) => {
    const id = `${collaborator.entityType}-${collaborator.user?.id || collaborator.userGroup?.id}`;
    const updated = {
      ...changes,
      [id]: {
        read: collaborator.read,
        write: collaborator.write
      }
    }

    setChanges(updated);
  }

  return (
    <Slider.Dialog show={show} width={'400px'}
      className={cx(styles.confirmationDialog)}
      onClose={onClose}
    >
      <Slider.Body className={styles.body}>
        <div className={styles.header}>
          <h5 className={styles.title}>collaborate</h5>
          <p className={styles.subtitle}>Decide who gets access. Remember, with great power comes great responsibility.</p>
        </div>
        <div className={styles.content}>
          <div className={styles.quickActions}>
            {!(readAll || writeAll) && (
              <FormCheck className={styles.collaborator} type="switch"
                label="Only Collaborators"
                checked={showCollaborators}
                onChange={() => setShowCollaborators(!showCollaborators)}
              />
            )}
            {!showCollaborators &&
              <div className={styles.edit}>
                <Checkbox className={styles.action}
                  label="All can view"
                  disabled={writeAll}
                  checked={readAll}
                  onChange={handleReadAllChange}
                />
                <div className={styles.separator}></div>
                <Checkbox className={styles.action}
                  label="All can edit"
                  checked={writeAll}
                  onChange={handleWriteAllChange}
                />
              </div>
            }
          </div>
         {!showCollaborators && <div className={styles.searchBar}>
            <SearchBar query={query} onSearch={handleQueryChange} />
          </div>}
          <div className={cx(styles.stacks, styles.content)}>
            <UserBrowser collection={collection}
              query={query || ""}
              showCollaborators={showCollaborators}
              readAll={readAll}
              writeAll={writeAll}
              changes={changes}
              onChange={handleChange}
            />
          </div>
        </div>
      </Slider.Body>
      <Slider.Actions>
        <Button
          variant="primary"
          icon="paperflite-tick"
          text={updating ? "Updating..." : "Update"}
          disabled={updating}
          onClick={handleFinish}
        />
        <Button
          variant="secondary"
          text="Cancel"
          disabled={updating}
          onClick={handleClose}
        />
      </Slider.Actions>
    </Slider.Dialog>
  );
}

interface UserBrowserProps {
  collection: Collection;
  query: string;
  showCollaborators: boolean;
  readAll: boolean;
  writeAll: boolean;
  changes: UpdateState;
  // eslint-disable-next-line no-unused-vars
  onChange: (collaborator: CollectionCollaborate) => void;
}

const UserBrowser: React.FC<UserBrowserProps> = ({ query, collection, showCollaborators, readAll, writeAll, changes, onChange }) => {
  const dispatch = useDispatch();

  const { loading, collaborators, hasMoreItems, nextPage, currentPage } = useSelector((state: any) => {
    const collaboratorState = _.get(state, `collections.collection['${collection.id}'].collaborators`);
    const collaborators = _.get(collaboratorState, 'items') || [];
    const loading = _.get(collaboratorState, 'loading');
    const pagination = _.get(collaboratorState, 'pagination');
    const totalPages = _.get(pagination, "totalPages", 0);
    const totalElements = _.get(pagination, "totalRecordsCount", 0);
    const hasMoreItems = _.get(pagination, "hasMoreItems");
    const currentPage = _.get(pagination, "currentPage", 0);

    let nextPage = currentPage + 1;
    if (nextPage >= totalPages) {
      nextPage = null;
    }

    return {
      loading,
      collaborators,
      totalElements,
      hasMoreItems,
      nextPage,
      currentPage
    }
  }, _.isEqual);

  const user = useSelector((state: any) => _.get(state, 'users.me.id'));

  const empty = _.size(collaborators) === 0 && !loading;

  useEffect(() => {
    // Fetch users and user groups
    fetchCollaboratorsForPage(0);
  }, [showCollaborators, query]);

  const fetchCollaboratorsForPage = (page) => {
    if (showCollaborators) {
      dispatch(fetchUserAccessForCollection({
        collection: collection.id!
      }));
    } else {
      dispatch(fetchCollaboratorsForCollection({
        collection: collection.id!,
        query: query,
        page: page,
        limit: 20
      }));
    }
  }

  const handleLoadCollaborators = () => {
    if (nextPage) {
      fetchCollaboratorsForPage(nextPage);
    }
  }


  const restrictUserGroup = useMemo(() => {
    const viewerType = _.get(collection, 'viewerType');
    if (viewerType === DEAL_ROOM_EXPERIENCE) {
      return true;
    }
    return false;
  }, [collection])


  if (empty) {
    return (
      <div className={styles.empty}>
        <EmptyMessage
          image={'/images/collections/empty_collaborators.svg'}
          heading="Uh oh!"
          message="The user(s) that you were looking for was not found."
        />
      </div>
    );
  }

  return (
    <div className={cx(styles.assetBrowser, styles.stack)}>
      <InfiniteScroll
        className={styles.scroller}
        loading={loading}
        hasMore={hasMoreItems}
        loadMore={handleLoadCollaborators}
        loader={() => {
          return (
            <>
              {_.times(currentPage < 0 ? 3 : 1, (index) => (
                <UserPlaceholder key={index} />
              ))}
            </>
          )
        }}
      >
        {collaborators.map((collaborator) => {
          const entityType = collaborator.entityType;

          if (entityType === 'user') {
            const owner = collaborator.user?.id === user;
            const id = `${entityType}-${collaborator.user?.id}`;
            const isDeactivated = !_.get(collaborator, 'user.enabled', true);
            const changed = changes[id];
            if (changed) {
              collaborator = {
                ...collaborator,
                read: changed.read,
                write: changed.write
              }
            }

            return (
              <UserRow key={collaborator.user?.id}
                collaborator={collaborator}
                owner={owner}
                readAll={readAll}
                writeAll={writeAll}
                onChange={onChange}
                isDeactivated={isDeactivated}
              />
            );
          } else if (entityType === 'user_group') {
            if (restrictUserGroup) return;
            const id = `${entityType}-${collaborator.userGroup?.id}`;
            const changed = changes[id];
            if (changed) {
              collaborator = {
                ...collaborator,
                read: changed.read,
                write: changed.write
              }
            }

            return <UserGroupRow key={collaborator.userGroup?.id}
              collaborator={collaborator}
              readAll={readAll}
              writeAll={writeAll}
              onChange={onChange}
            />;
          }

          return false;
        })}
      </InfiniteScroll>
    </div>
  );
}

interface UserRowProps {
  collaborator: CollectionCollaborate;
  owner?: boolean;
  readAll: boolean;
  writeAll: boolean;
  // eslint-disable-next-line no-unused-vars
  onChange: (collaborator: CollectionCollaborate) => void;
  isDeactivated?: boolean;
}

const UserRow: React.FC<UserRowProps> = ({ collaborator, readAll, writeAll, owner, onChange, isDeactivated }) => {
  const canRead = collaborator.read || readAll || owner!;
  const canWrite = collaborator.write || writeAll || owner!;
  const name = collaborator.user?.firstName + ' ' + collaborator.user?.lastName;
  const [clamped, setClamped] = React.useState<boolean>(false);

  const handleReadChange = (value) => {
    onChange({
      ...collaborator,
      read: value
    });
  }

  const handleWriteChange = (value) => {
    onChange({
      ...collaborator,
      read: value ? true : collaborator.read,
      write: value
    });
  }

  const handleTextOverflow = (state) => {
    const { clamped } = state;
    setClamped(clamped);
  }

  return (
    <div className={cx(styles.row, styles.user, { [styles.owner]: owner })}>
      <div
        className={cx(styles.details, {
          [styles.deactivatedUser]: isDeactivated,
        })}
      >
        <i
          className={cx({
            "paperflite-user-settings": !isDeactivated,
            "fa fa-ban": isDeactivated,
          })} />
        <div className={styles.nameWrapper}>
          <Tooltip content={name} placement="top-start">
            <span>
              <Truncate
                className={cx(styles.name, { [styles.deactivatedName]: isDeactivated })}
                lines={1}
                width={200}
              >
                {name}
              </Truncate>
            </span>
          </Tooltip>
          {isDeactivated && (
            <span className={styles.deactivatedTag}>&nbsp;(Deactivated)</span>
          )}
          {owner && <div className={styles.tag}>OWNER</div>}
        </div>
      </div>
      {!isDeactivated && (
      <div className={styles.actions}>
        <Checkbox className={styles.action}
          label="view"
          checked={canRead || canWrite}
          disabled={owner || readAll || canWrite}
          onChange={handleReadChange}

        />
        <div className={styles.separator}></div>
        <Checkbox className={styles.action}
          label="edit"
          checked={canWrite}
          disabled={owner || writeAll}
          onChange={handleWriteChange}
        />
      </div>
      )}   
    </div>
  );
}

const UserGroupRow: React.FC<UserRowProps> = ({ collaborator, readAll, writeAll, onChange }) => {
  const canRead = collaborator.read || readAll;
  const canWrite = collaborator.write || writeAll;
  const name = collaborator.userGroup?.name;
  const [clamped, setClamped] = React.useState<boolean>(false);

  const handleReadChange = (value) => {
    onChange({
      ...collaborator,
      read: value
    });
  }

  const handleWriteChange = (value) => {
    onChange({
      ...collaborator,
      read: value ? true : collaborator.read,
      write: value
    });
  }

  const handleTextOverflow = (state) => {
    const { clamped } = state;
    setClamped(clamped);
  }

  return (
    <div className={styles.row}>
      <div className={styles.details}>
        <i className="paperflite-group"></i>
        <Tooltip content={name} placement="top-start">
          <span>
            <Truncate className={styles.name} lines={1} width={200}>
              {name}
            </Truncate>
          </span>
        </Tooltip>
      </div>
      <div className={styles.actions}>
        <Checkbox className={styles.action}
          label="view"
          checked={canRead || canWrite}
          disabled={readAll || canWrite}
          onChange={handleReadChange}

        />
        <div className={styles.separator}></div>
        <Checkbox className={styles.action}
          label="edit"
          checked={canWrite}
          disabled={writeAll}
          onChange={handleWriteChange}
        />
      </div>
    </div>
  );
}

const UserPlaceholder: React.FC = () => {
  return (
    <div className={cx(styles.row, styles.placeholder)}>
      <div className={styles.details}>
        <div className={cx(styles.name, styles.block)}></div>
      </div>
      <div className={styles.actions}>
        <div className={cx(styles.action)}>
          <div className={cx(styles.circle, styles.block)}></div>
          <div className={cx(styles.text, styles.block)}></div>
        </div>
        <div className={styles.separator}></div>
        <div className={cx(styles.action)}>
          <div className={cx(styles.circle, styles.block)}></div>
          <div className={cx(styles.text, styles.block)}></div>
        </div>
      </div>
    </div>
  );
}